import { useState, useEffect } from 'react'
import { Link, useNavigate, Routes, Route, useLocation } from 'react-router-dom'
import api from '../services/api'
import { 
  LayoutDashboard, 
  FileText, 
  Layers, 
  Users, 
  Image as ImageIcon, 
  MessageSquare, 
  Settings, 
  LogOut,
  ChevronRight,
  Briefcase,
  Landmark,
  Menu,
  X
} from 'lucide-react'
import useAuthStore from '../store/authStore'

import AdminProgrammes from './AdminProgrammes'
import AdminProjets from './AdminProjets'
import AdminSeminaires from './AdminSeminaires'
import AdminExperts from './AdminExperts'
import AdminMessages from './AdminMessages'
import AdminParametres from './AdminParametres'


const SidebarItem = ({ icon: Icon, label, path, active }) => (
  <Link 
    to={path} 
    className={`flex items-center gap-3 px-6 py-4 transition-all ${
      active ? 'bg-white/10 border-l-4 border-cidh-gold text-white' : 'text-gray-400 hover:text-white hover:bg-white/5'
    }`}
  >
    <Icon size={20} />
    <span className="font-medium">{label}</span>
    {active && <ChevronRight size={16} className="ml-auto" />}
  </Link>
)

const Dashboard = () => {
  const { user, logout } = useAuthStore()
  const navigate = useNavigate()
  const location = useLocation()
  const [isSidebarOpen, setIsSidebarOpen] = useState(false)

  // Close sidebar on mobile when navigating
  useEffect(() => {
    setIsSidebarOpen(false)
  }, [location.pathname])

  const handleLogout = () => {
    logout()
    navigate('/admin/login')
  }

  const menuItems = [
    { icon: LayoutDashboard, label: 'Tableau de bord', path: '/admin/dashboard' },
    { icon: Layers, label: 'Programmes', path: '/admin/programmes' },
    { icon: Briefcase, label: 'Projets', path: '/admin/projets' },
    { icon: Landmark, label: 'Séminaires', path: '/admin/seminaires' }, // Added Séminaires
    { icon: Users, label: 'Experts', path: '/admin/experts' },
    { icon: MessageSquare, label: 'Messages', path: '/admin/messages' },
    { icon: Settings, label: 'Paramètres', path: '/admin/parametres' },
  ]

  return (
    <div className="flex min-h-screen bg-gray-100">
      {/* Overlay for mobile */}
      {isSidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-20 lg:hidden transition-opacity"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`w-64 bg-cidh-blue text-white flex flex-col fixed inset-y-0 left-0 transform transition-transform duration-300 ease-in-out z-30 ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 shadow-2xl overflow-y-auto`}>
        <div className="p-6 lg:p-8 mb-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img 
              src="/logo.png" 
              alt="CIDH Consulting Logo" 
              className="h-10 w-auto object-contain"
            />
            <span className="text-lg font-bold font-montserrat tracking-tight">Admin</span>
          </Link>
          <button 
            className="lg:hidden text-gray-300 hover:text-white"
            onClick={() => setIsSidebarOpen(false)}
          >
            <X size={24} />
          </button>
        </div>

        <nav className="flex-grow">
          {menuItems.map((item) => (
            <SidebarItem 
              key={item.label} 
              {...item} 
              active={location.pathname === item.path}
            />
          ))}
        </nav>

        <div className="p-4 border-t border-white/10">
          <button 
            onClick={handleLogout}
            className="flex items-center gap-3 w-full px-4 py-3 text-gray-400 hover:text-white hover:bg-red-500/10 rounded-lg transition-all"
          >
            <LogOut size={20} />
            <span className="font-medium">Déconnexion</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-grow w-full lg:ml-64 p-4 lg:p-8 transition-all duration-300">
        <header className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6 lg:mb-10 bg-white p-4 lg:p-6 rounded-2xl shadow-sm">
          <div className="flex items-center gap-4">
            <button 
              className="lg:hidden p-2 hover:bg-gray-100 rounded-lg text-cidh-blue transition-colors"
              onClick={() => setIsSidebarOpen(true)}
            >
              <Menu size={24} />
            </button>
            <div>
              <h2 className="text-lg lg:text-xl font-bold text-cidh-blue font-montserrat">Bienvenue, {user?.prenom}</h2>
              <p className="text-xs lg:text-sm text-gray-500">Gérez le contenu de votre site institutionnel</p>
            </div>
          </div>
          <div className="flex items-center gap-4 self-end sm:self-auto">
              <div className="w-10 h-10 bg-cidh-gray-light rounded-full border flex items-center justify-center font-bold text-cidh-blue">
                {user?.nom?.charAt(0)}
              </div>
          </div>
        </header>

        <div className="animate-fade-in overflow-x-hidden md:overflow-visible">
          <Routes>
            <Route path="/dashboard" element={<AdminStats />} />
            <Route path="/programmes" element={<AdminProgrammes />} />
            <Route path="/projets" element={<AdminProjets />} />
            <Route path="/seminaires" element={<AdminSeminaires />} />
            <Route path="/experts" element={<AdminExperts />} />
            <Route path="/messages" element={<AdminMessages />} />
            <Route path="/parametres" element={<AdminParametres />} />
            <Route path="*" element={<div className="text-center py-20 text-gray-400">Section en cours de développement...</div>} />
          </Routes>
        </div>
      </main>
    </div>
  )
}


// Sub-component for Dashboard Stats
const AdminStats = () => {
    const [stats, setStats] = useState({
        programmes: 0,
        projets: 0,
        experts: 0,
        messages_non_lus: 0
    })
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        const fetchStats = async () => {
            try {
                const response = await api.get('/dashboard/stats')
                setStats(response.data)
            } catch (error) {
                console.error("Erreur lors de la récupération des statistiques:", error)
            } finally {
                setLoading(false)
            }
        }

        fetchStats()
    }, [])

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64 text-cidh-blue">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cidh-blue"></div>
            </div>
        )
    }

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <StatCard title="Programmes" value={stats.programmes} icon={Layers} color="bg-blue-500" />
            <StatCard title="Projets" value={stats.projets} icon={Briefcase} color="bg-green-500" />
            <StatCard title="Experts" value={stats.experts} icon={Users} color="bg-purple-500" />
            <StatCard title="Nouveaux Messages" value={stats.messages_non_lus} icon={MessageSquare} color="bg-orange-500" />
        </div>
    )
}

const StatCard = ({ title, value, icon: Icon, color }) => (
    <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex items-center gap-6">
        <div className={`${color} p-4 rounded-xl text-white shadow-lg`}>
            <Icon size={24} />
        </div>
        <div>
            <p className="text-sm font-medium text-gray-500">{title}</p>
            <p className="text-2xl font-bold text-cidh-blue font-montserrat">{value}</p>
        </div>
    </div>
)

export default Dashboard
